/* ============================================================
   DIETMONSTA · Ready Meals pack builder

   Client-side pack assembly, then a single /cart/add.js request
   containing every chosen meal as its own line item.

   The pack rule (exactly 10 or exactly 20) is enforced here for UX,
   but this is a convenience layer, not a guarantee — anyone can POST
   to /cart/add directly. Server-side enforcement of the client's
   "minimum 10 units" rule needs a cart validation function, which on
   the Grow plan means a public App Store app. See PROJECT.md.
   ============================================================ */

class DMReadyMeals extends HTMLElement {
  connectedCallback() {
    this.strings = this.#readStrings();
    this.packSizes = (this.dataset.packSizes || '10,20')
      .split(',')
      .map((n) => parseInt(n, 10))
      .filter((n) => n > 0);

    this.packSize = this.packSizes[0] || 10;
    this.selection = new Map(); // variantId (string) -> qty

    this.cards = Array.from(this.querySelectorAll('[data-dm-meal]'));

    // The counter, progress bar, total and submit button each appear twice:
    // once in the desktop sidecart and once in the mobile sticky bars. All
    // instances are kept in sync, so these are collections, not single nodes.
    const all = (sel) => Array.from(this.querySelectorAll(sel));
    this.els = {
      filled: all('[data-dm-filled]'),
      target: all('[data-dm-target]'),
      progress: all('[data-dm-progress]'),
      total: all('[data-dm-total]'),
      submit: all('[data-dm-submit]'),
      // Errors render in both the sidecart and the mobile CTA — the sidecart
      // is display:none below 990px, so a single node would leave mobile
      // users with a silently failing button.
      error: all('[data-dm-error]'),
      list: this.querySelector('[data-dm-list]'),
      empty: this.querySelector('[data-dm-empty]'),
      live: this.querySelector('[data-dm-live]'),
    };

    this.#bind();
    this.#render();
  }

  #readStrings() {
    const node = this.querySelector('[data-dm-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  #bind() {
    this.addEventListener('click', (event) => {
      const sizeBtn = event.target.closest('[data-dm-pick-size]');
      if (sizeBtn) return this.#pickSize(parseInt(sizeBtn.dataset.dmPickSize, 10));

      if (event.target.closest('[data-dm-change-size]')) return this.#goToStep('size');

      const card = event.target.closest('[data-dm-meal]');
      if (card) {
        if (event.target.closest('[data-dm-add], [data-dm-inc]')) return this.#bump(card, +1);
        if (event.target.closest('[data-dm-dec]')) return this.#bump(card, -1);
      }

      if (event.target.closest('[data-dm-submit]')) return this.#submit();
    });
  }

  #pickSize(size) {
    if (!Number.isFinite(size) || size <= 0) return;
    this.packSize = size;
    this.els.target.forEach((el) => {
      el.textContent = String(size);
    });
    this.#goToStep('build');
    this.#render();
  }

  #goToStep(step) {
    this.dataset.step = step;
    // Keep the newly revealed step in view rather than leaving the user
    // scrolled to wherever the previous step happened to end.
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #bump(card, delta) {
    const id = card.dataset.variantId;
    const current = this.selection.get(id) || 0;
    const next = current + delta;

    // Never let the pack exceed its size — the + buttons disable at the
    // limit, but a fast double-click can still land here.
    if (next < 0) return;
    if (delta > 0 && this.#filled() >= this.packSize) return;

    if (next === 0) this.selection.delete(id);
    else this.selection.set(id, next);

    this.#render();
  }

  #filled() {
    let n = 0;
    this.selection.forEach((qty) => {
      n += qty;
    });
    return n;
  }

  #subtotal() {
    let cents = 0;
    this.selection.forEach((qty, id) => {
      const card = this.cards.find((c) => c.dataset.variantId === id);
      if (card) cents += parseInt(card.dataset.price, 10) * qty;
    });
    return cents;
  }

  /* Format cents using the store's currency. Returns { symbol, amount }
     so the price can render with the symbol at a smaller size, as the
     design does. Falls back to a plain number if Intl is unavailable. */
  #money(cents) {
    const currency = this.strings.currency || 'MYR';
    const value = cents / 100;
    try {
      const parts = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(value);

      const symbol = parts
        .filter((p) => p.type === 'currency')
        .map((p) => p.value)
        .join('');
      // Matches the card price display: whole amounts drop the ".00",
      // fractional ones keep their cents.
      const fraction = parts.find((p) => p.type === 'fraction');
      const dropDecimals = fraction && /^0+$/.test(fraction.value);
      const amount = parts
        .filter((p) => p.type !== 'currency' && p.type !== 'literal')
        .filter((p) => !dropDecimals || (p.type !== 'decimal' && p.type !== 'fraction'))
        .map((p) => p.value)
        .join('');

      return { symbol, amount };
    } catch {
      return { symbol: '', amount: Number.isInteger(value) ? String(value) : value.toFixed(2) };
    }
  }

  #render() {
    const filled = this.#filled();
    const remaining = this.packSize - filled;
    const atLimit = filled >= this.packSize;

    // --- cards ---
    this.cards.forEach((card) => {
      const qty = this.selection.get(card.dataset.variantId) || 0;
      const addBtn = card.querySelector('[data-dm-add]');
      const stepper = card.querySelector('[data-dm-stepper]');
      const badge = card.querySelector('[data-dm-qty-badge]');

      if (!addBtn || !stepper) return; // sold-out card

      card.classList.toggle('is-selected', qty > 0);
      addBtn.hidden = qty > 0;
      stepper.hidden = qty === 0;

      if (badge) {
        badge.hidden = qty === 0;
        const badgeVal = card.querySelector('[data-dm-qty-badge-val]');
        if (badgeVal) badgeVal.textContent = String(qty);
      }

      const qtyEl = card.querySelector('[data-dm-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);

      // Block further additions once the pack is full.
      addBtn.disabled = atLimit;
      const incBtn = card.querySelector('[data-dm-inc]');
      if (incBtn) incBtn.disabled = atLimit;
    });

    // --- counter + progress ---
    this.els.filled.forEach((el) => {
      el.textContent = String(filled);
    });
    const pct = this.packSize > 0 ? Math.min(100, (filled / this.packSize) * 100) : 0;
    this.els.progress.forEach((el) => {
      el.style.width = `${pct}%`;
    });

    // --- selected list ---
    this.#renderList();

    // --- total ---
    const { symbol, amount } = this.#money(this.#subtotal());
    this.els.total.forEach((el) => {
      el.innerHTML = '';
      if (symbol) {
        const sym = document.createElement('span');
        sym.className = 'dm-b__cur';
        sym.textContent = symbol;
        el.appendChild(sym);
      }
      const amt = document.createElement('span');
      amt.textContent = amount;
      el.appendChild(amt);
    });

    // --- submit ---
    const ready = filled === this.packSize;
    let label;
    if (ready) {
      label = this.strings.packFull || 'Add pack to cart';
    } else if (remaining > 0) {
      label = (this.strings.needMore || 'Add [n] more').replace('[n]', String(remaining));
    } else {
      label = (this.strings.tooMany || 'Remove [n]').replace('[n]', String(Math.abs(remaining)));
    }
    this.els.submit.forEach((el) => {
      el.disabled = !ready;
      // The mobile CTA keeps a short fixed label — there is no room beside
      // the price for "Add 5 more to fill your pack", and the sticky top
      // bar already shows the running count.
      if (!el.hasAttribute('data-dm-static-label')) el.textContent = label;
    });

    // --- screen reader announcement ---
    if (this.els.live) {
      this.els.live.textContent = (this.strings.progress || '[filled] of [target] selected')
        .replace('[filled]', String(filled))
        .replace('[target]', String(this.packSize));
    }
  }

  #renderList() {
    const { list, empty } = this.els;
    if (!list) return;

    list.querySelectorAll('[data-dm-row]').forEach((row) => row.remove());

    if (this.selection.size === 0) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    this.selection.forEach((qty, id) => {
      const card = this.cards.find((c) => c.dataset.variantId === id);
      if (!card) return;

      const name = card.querySelector('.dm-b__name')?.textContent?.trim() || '';
      const base = card.querySelector('.dm-b__meta')?.textContent?.trim() || '';

      const row = document.createElement('div');
      row.className = 'dm-b__side-row';
      row.setAttribute('data-dm-row', '');

      const nameEl = document.createElement('span');
      nameEl.className = 'dm-b__side-row-name';
      nameEl.textContent = name;
      if (base) {
        const baseEl = document.createElement('span');
        baseEl.className = 'dm-b__side-row-base';
        baseEl.textContent = ` · ${base}`;
        nameEl.appendChild(baseEl);
      }

      const qtyEl = document.createElement('span');
      qtyEl.className = 'dm-b__side-row-qty';
      qtyEl.textContent = `×${qty}`;

      row.append(nameEl, qtyEl);
      list.appendChild(row);
    });
  }

  async #submit() {
    if (this.#filled() !== this.packSize) return;

    const { submit, error } = this.els;
    error.forEach((el) => {
      el.textContent = '';
    });
    submit.forEach((el) => {
      el.disabled = true;
    });

    // One id per pack so the cart can group these lines back together.
    const packId = `rm-${this.packSize}-${Date.now()}`;
    const packLabel = `${this.strings.packProperty || 'Ready Meals pack'} (${this.packSize})`;

    const items = [];
    this.selection.forEach((qty, id) => {
      items.push({
        id: Number(id),
        quantity: qty,
        properties: {
          _dm_pack_id: packId,
          _dm_pack: packLabel,
          /* The cart validates each group against its own minimum, so it
             has to be told what this group's minimum was. Without it the
             cart falls back to one flat rule for every order. */
          _dm_min: String(this.packSize),
        },
      });
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'add_failed');
      }

      // Always reset the builder — the pack now lives in the cart.
      this.selection.clear();
      this.#render();
      this.dispatchEvent(new CustomEvent('dm:pack-added', { bubbles: true, detail: { packId } }));

      // Prefer the drawer: it shows progress toward the order minimum and
      // the free-delivery threshold, which a redirect to /cart would not.
      if (document.querySelector('dm-cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dm:cart-added'));
      } else if (this.dataset.cartRedirect !== 'stay') {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } catch (err) {
      const message =
        err.message && err.message !== 'add_failed'
          ? err.message
          : this.strings.genericError || 'Sorry, we could not add these to your cart.';
      error.forEach((el) => {
        el.textContent = message;
      });
      submit.forEach((el) => {
        el.disabled = false;
      });
    }
  }
}

if (!customElements.get('dm-ready-meals')) {
  customElements.define('dm-ready-meals', DMReadyMeals);
}
